import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Pagination, Alert, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { Event } from '../types';
import EventCard from '../components/EventCard';
import SkeletonLoader, { EventCardSkeleton, EventsPageHeroSkeleton } from '../components/common/SkeletonLoader';
import SEO from '../components/common/SEO';
import './EventsPage.scss';

interface EventsResponse {
  events: Event[];
  pagination: {
    current_page: number;
    total_pages: number;
    total_events: number;
    has_next: boolean;
    has_previous: boolean;
    next_page: number | null;
    previous_page: number | null;
  };
  filters: {
    search: string;
    status: string;
    ordering: string;
    country_code: string;
  };
}

const EventsPage: React.FC = () => {
  const [eventsData, setEventsData] = useState<EventsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [ordering, setOrdering] = useState('-starts');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchEvents = async (page: number = 1, forceRefresh: boolean = false) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        search: searchQuery || undefined,
        status: statusFilter || undefined,
        ordering
      };
      
      console.log('Fetching events with params:', params);
      const data = await apiService.events.getEvents(params, forceRefresh);
      console.log('Events data received:', data);
      setEventsData(data);
      setCurrentPage(page);
    } catch (err) {
      setError('Failed to load events');
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents(1);
  }, []);

  // Listen for country changes and refresh events
  useEffect(() => {
    const handleCountryChange = () => {
      console.log('Country changed, refreshing events...');
      // Clear events cache and force refresh
      apiService.cache.clearByPattern('^events');
      // Add a small delay to ensure session is saved on backend
      setTimeout(() => {
        fetchEvents(1, true);
      }, 200);
    };

    // Listen for custom country change event
    window.addEventListener('countryChanged', handleCountryChange as EventListener);
    
    return () => {
      window.removeEventListener('countryChanged', handleCountryChange as EventListener);
    };
  }, [searchQuery, statusFilter, ordering]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchEvents(1, true);
  };

  const handleStatusChange = (status: string) => {
    setStatusFilter(status);
    fetchEvents(1, true);
  };

  const handleOrderingChange = (newOrdering: string) => {
    setOrdering(newOrdering);
    fetchEvents(1, true);
  };

  const handlePageChange = (page: number) => {
    fetchEvents(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (!eventsData?.pagination || eventsData.pagination.total_pages <= 1) {
      return null;
    }

    const { current_page, total_pages, has_previous, has_next } = eventsData.pagination;
    const items = [];

    // Previous button
    items.push(
      <Pagination.Prev
        key="prev"
        disabled={!has_previous}
        onClick={() => has_previous && handlePageChange(current_page - 1)}
      />
    );

    // Page numbers
    const startPage = Math.max(1, current_page - 2);
    const endPage = Math.min(total_pages, current_page + 2);

    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === current_page}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Next button
    items.push(
      <Pagination.Next
        key="next"
        disabled={!has_next}
        onClick={() => has_next && handlePageChange(current_page + 1)}
      />
    );

    return (
      <div className="d-flex justify-content-center mt-4">
        <Pagination>{items}</Pagination>
      </div>
    );
  };

  if (loading && !eventsData) {
    return (
      <div className="events-page">
        <Container>
          <Row className="mb-5">
            <Col>
              <EventsPageHeroSkeleton />
            </Col>
          </Row>
          <Row className="g-4">
            {Array.from({ length: 6 }).map((_, index) => (
              <Col lg={4} md={6} key={index}>
                <EventCardSkeleton />
              </Col>
            ))}
          </Row>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  const pageTitle = searchQuery 
    ? `Search Events: ${searchQuery} | Quicket`
    : 'Browse All Events | Quicket';
  const pageDescription = searchQuery
    ? `Search results for events matching "${searchQuery}" on Quicket`
    : 'Browse and discover amazing events happening near you. Find concerts, conferences, workshops, and more on Quicket.';

  return (
    <div className="events-page">
      <SEO
        title={pageTitle}
        description={pageDescription}
        type="website"
      />
      <Container>

        {/* Filters Section */}
        <Row className="mb-5">
          <Col>
            <div className="filters-section">
              <Form onSubmit={handleSearch} className="filters-form">
                <Row className="g-3">
                  <Col lg={5} md={6}>
                    <Form.Group>
                      <Form.Control
                        type="text"
                        placeholder="Search events, venues..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="form-control-search"
                      />
                    </Form.Group>
                  </Col>
                  <Col lg={2} md={3}>
                    <Form.Select
                      value={statusFilter}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      className="form-select-filter"
                    >
                      <option value="">All Events</option>
                      <option value="ongoing">Live Now</option>
                      <option value="scheduled">Upcoming</option>
                      <option value="finished">Past Events</option>
                    </Form.Select>
                  </Col>
                  <Col lg={2} md={3}>
                    <Form.Select
                      value={ordering}
                      onChange={(e) => handleOrderingChange(e.target.value)}
                      className="form-select-filter"
                    >
                      <option value="-starts">Newest First</option>
                      <option value="starts">Oldest First</option>
                    </Form.Select>
                  </Col>
                  <Col lg={3} md={12}>
                    <div className="d-flex gap-2">
                      <Button type="submit" variant="primary" className="btn-filter-search flex-fill">
                        <i className="fas fa-search me-2"></i>
                        Search
                      </Button>
                      {(searchQuery || statusFilter) && (
                        <Button
                          type="button"
                          variant="outline-secondary"
                          className="btn-clear-filters"
                          onClick={() => {
                            setSearchQuery('');
                            setStatusFilter('');
                            fetchEvents(1, true);
                          }}
                        >
                          <i className="fas fa-times"></i>
                        </Button>
                      )}
                    </div>
                  </Col>
                </Row>
              </Form>
            </div>
          </Col>
        </Row>

        {/* Events Grid */}
        {eventsData?.events && eventsData.events.length > 0 ? (
          <>
            <Row className="g-4 mb-5">
              {eventsData.events.map((event) => (
                <Col lg={4} md={6} key={event.id}>
                  <EventCard event={event} />
                </Col>
              ))}
            </Row>
            {renderPagination()}
          </>
        ) : (
          <Row>
            <Col>
              <div className="empty-state">
                <div className="empty-state-content">
                  <div className="empty-state-icon">
                    <i className="fas fa-calendar-times"></i>
                  </div>
                  <h3 className="empty-state-title">No Events Found</h3>
                  <p className="empty-state-description">
                    {searchQuery || statusFilter
                      ? 'We couldn\'t find any events matching your criteria. Try adjusting your search or filters.'
                      : 'Check back later for exciting new events!'}
                  </p>
                  {(searchQuery || statusFilter) && (
                    <Button
                      variant="primary"
                      className="btn-clear-filters"
                      onClick={() => {
                        setSearchQuery('');
                        setStatusFilter('');
                        fetchEvents(1, true);
                      }}
                    >
                      <i className="fas fa-times me-2"></i>
                      Clear All Filters
                    </Button>
                  )}
                </div>
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default EventsPage;
